import { useEffect, useState } from 'react'
import { tmdbGet } from '../lib/tmdb'
import { useMyServices } from '../lib/streaming'

// Curated provider colors for well-known services
const PROVIDER_COLORS = {
  8:   'var(--red)', // Netflix
  9:   '#00A8E0', // Amazon
  337: '#6772e5', // Disney+
  350: '#2496D5', // Apple TV+
  384: '#0036ff', // HBO Max
  15:  '#1db954', // Hulu
  2303: '#ff6c00', // Paramount+ Premium
  2616: '#ff6c00', // Paramount+ Essential
  386: '#5622d4', // Peacock
  43:  '#eb0028', // Starz
  37:  '#cc0000', // Showtime
  283: '#e77b2e', // Crunchyroll
  546: '#ff6600', // fuboTV
  41:  '#00d9ff', // Philo
}

// ── WatchProviders (compact) ──────────────────────────────────────────────────
// Displays streaming availability for a show, used in:
//   - ShowDetail About tab (compact=true, the default)
//   - ShowDetail More tab (compact=false, full breakdown)

// `season`: the season you're about to watch. TMDB's show-level list is the union
// of every season's carriers (Skinwalker Ranch: Netflix has S1–4, S7 is Philo-only,
// the row said Netflix — Jason 2026-11-01). With a season we ask for that season's
// list first and fall back to the union only when TMDB has nothing for it.
export function WatchProviders({ showId, season, country = 'US', compact = true, heading = true, providers: propProviders }) {
  const [providers, setProviders] = useState(propProviders || undefined)
  const [perSeason, setPerSeason] = useState(false)
  const myServices = useMyServices()

  useEffect(() => {
    if (propProviders) { setProviders(propProviders); return }
    if (!showId) return
    let alive = true
    const n = Number(season)
    const hasStream = r => r && ((r.flatrate || []).length + (r.free || []).length + (r.ads || []).length) > 0
    const union = () => tmdbGet(`/tv/${showId}/watch/providers`).then(d => d.results?.[country] || null)
    const run = Number.isFinite(n) && n >= 1
      ? tmdbGet(`/tv/${showId}/season/${n}/watch/providers`).then(d => d.results?.[country]).then(r => hasStream(r) ? { r, per: true } : union().then(r => ({ r, per: false }))).catch(() => union().then(r => ({ r, per: false })))
      : union().then(r => ({ r, per: false }))
    run.then(({ r, per }) => { if (alive) { setProviders(r); setPerSeason(per) } })
       .catch(() => { if (alive) { setProviders(null); setPerSeason(false) } })
    return () => { alive = false }
  }, [showId, season, country, propProviders])

  if (providers === undefined) return null
  const seasonTag = perSeason ? `S${Number(season)}` : null

  const stream = providers?.flatrate || []
  const free   = (providers?.free || []).concat(providers?.ads || [])
  const rent   = providers?.rent || []
  const buy    = providers?.buy || []
  const all    = [...stream, ...free]
  if (all.length + rent.length + buy.length === 0) {
    if (compact) return null
    return <div style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.5, fontFamily: 'var(--font)', marginBottom: 14 }}>No streaming, rental or purchase options listed for the US right now.</div>
  }

  // Identify which services the user subscribes to
  const myIds = new Set((myServices?.list || []).map(s => s.id))
  const myStream = stream.filter(p => myIds.has(p.provider_id))

  if (compact) {
    // Compact mode: a single tidy row — label, up to 3 logos, overflow count, link.
    // Kept to ONE line (no wrap): a 4-logo wrap left "Stream on" stranded against a
    // two-line block in the ShowDetail hero (Jason 2026-07-23).
    const MAX = 3
    const show = stream.slice(0, MAX)
    if (!show.length) return null
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, fontFamily:'var(--font)' }}>
        <span style={{ color:'var(--text-dim)', fontSize:11, fontWeight:500, flexShrink:0 }}>{seasonTag ? `${seasonTag} on` : 'Stream on'}</span>
        <div style={{ display:'flex', gap:5, flexShrink:0 }}>
          {show.map(p => {
            const isMine = myIds.has(p.provider_id)
            const color = PROVIDER_COLORS[p.provider_id]
            return (
              <div key={p.provider_id} title={p.provider_name} style={{ position:'relative' }}>
                <div style={{
                  width:30, height:30, borderRadius:'var(--radius-xs)', overflow:'hidden', flexShrink:0,
                  border: isMine ? `1.5px solid ${color || 'var(--amber)'}` : '1px solid var(--border)',
                  boxShadow: isMine ? `0 0 8px ${color || 'rgba(var(--amber-rgb),0.4)'}40` : 'none',
                  transition:'all 0.15s',
                }}>
                  <img src={`https://image.tmdb.org/t/p/w92${p.logo_path}`} alt={p.provider_name}
                    style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} draggable="false"/>
                </div>
                {isMine && (
                  <div style={{
                    position:'absolute', top:-3, right:-3, width:10, height:10,
                    borderRadius:'var(--radius-full)', background:'var(--green)',
                    border:'1.5px solid var(--bg)', zIndex:1,
                  }}/>
                )}
              </div>
            )
          })}
        </div>
        {stream.length > MAX && (
          <span style={{ color:'var(--text-dim)', fontSize:11, flexShrink:0 }}>+{stream.length - MAX}</span>
        )}
        {myStream.length > 0 && (
          <span style={{ color:'var(--green)', fontSize:10, fontWeight:600 }}>✓ In your plan</span>
        )}
        {Number(season) >= 1 ? (
          <span style={{ color:'var(--amber)', fontSize:11, fontWeight:600, marginLeft:'auto', flexShrink:0 }}>All seasons ›</span>
        ) : providers.link && (
          <a href={providers.link} target="_blank" rel="noreferrer" className="ui-hit"
            style={{ color:'var(--text-dim)', fontSize:10, textDecoration:'none', marginLeft:'auto', flexShrink:0 }}>
            More →
          </a>
        )}
      </div>
    )
  }

  // Full mode (More tab)
  // Every logo is a real link (JustWatch page via TMDB) — display-only logos
  // read as broken (2026-06-11 feedback).
  const Row = ({ label, items, accent }) => items.length === 0 ? null : (
    <div style={{ marginBottom:10 }}>
      <div style={{ color:accent || 'var(--text-dim)', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>{label}</div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        {items.map(p => {
          const isMine = myIds.has(p.provider_id)
          const Wrap = providers.link ? 'a' : 'div'
          return (
            <Wrap key={p.provider_id} title={`${p.provider_name}, see all options`}
              {...(providers.link ? { href: providers.link, target:'_blank', rel:'noreferrer' } : {})}
              style={{ position:'relative', cursor: providers.link ? 'pointer' : 'default', textDecoration:'none' }}>
              <div style={{
                width:44, height:44, borderRadius:10, overflow:'hidden',
                border: isMine ? '1.5px solid var(--amber)' : '1px solid var(--border)',
              }}>
                <img src={`https://image.tmdb.org/t/p/w92${p.logo_path}`} alt={p.provider_name}
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} draggable="false"/>
              </div>
              {isMine && <div style={{ position:'absolute', top:-3, right:-3, width:10, height:10, borderRadius:'var(--radius-full)', background:'var(--green)', border:'1.5px solid var(--bg)', zIndex:1 }}/>}
            </Wrap>
          )
        })}
      </div>
    </div>
  )

  return (
    <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'12px 14px', marginBottom:14, fontFamily:'var(--font)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent: (heading || seasonTag) ? 'space-between' : 'flex-end', marginBottom:10 }}>
        {heading ? <div style={{ color:'var(--text)', fontWeight:600, fontSize:13 }}>Where to watch{seasonTag ? ` · ${seasonTag}` : ''}</div>
          : seasonTag ? <div style={{ color:'var(--text-dim)', fontSize:12 }}>Season {Number(season)} · earlier seasons can live elsewhere</div> : null}
        {providers.link && (
          <a href={providers.link} target="_blank" rel="noreferrer" className="ui-hit"
            style={{ color:'var(--amber)', fontSize:12, textDecoration:'none', fontWeight:500, padding:'6px 0' }}>
            All options →
          </a>
        )}
      </div>
      <Row label="Included in subscription" items={stream} accent="var(--green)" />
      <Row label="Free / With ads"          items={free}   accent="var(--green)" />
      <Row label="Rent"                     items={rent}   accent="var(--text-muted)" />
      <Row label="Buy"                      items={buy}    accent="var(--text-muted)" />
      <div style={{ color:'var(--text-dim)', fontSize:11, marginTop:4 }}>Powered by JustWatch via TMDB · US availability</div>
    </div>
  )
}
